import {
  APP_NAME,
  DEFAULT_USER_TYPE,
  EVENT_NAMES,
  PARAM_KEYS,
} from "./constants";

const { resolveBrandFromHostname } = require("../../../shared/branding");

const DEBUG_STORAGE_KEY = "hh_analytics_debug";
const BLOCKED_KEYS = new Set([
  "name",
  "email",
  "phone",
  "street",
  "address",
  "company_name",
  "description",
  "message",
  "freeform_text",
]);

let persistentContext = {};
let userContext = {
  [PARAM_KEYS.USER_TYPE]: DEFAULT_USER_TYPE,
};
let pageContext = {};

function getGtag() {
  if (typeof window === "undefined") {
    return null;
  }

  return typeof window.gtag === "function" ? window.gtag : null;
}

function isDebugEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  const fromStorage = window.localStorage.getItem(DEBUG_STORAGE_KEY);

  if (fromStorage === "1") {
    return true;
  }

  return typeof process !== "undefined" && process.env.NODE_ENV !== "production";
}

function debugLog(eventName, payload) {
  if (!isDebugEnabled()) {
    return;
  }

  console.info(`[analytics:${APP_NAME}] ${eventName}`, payload);
}

function looksLikePii(value) {
  return /@/.test(value) || /\+?\d[\d\s().-]{7,}\d/.test(value);
}

function sanitizeValue(key, value) {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (Array.isArray(value)) {
    const joined = value
      .map((entry) => sanitizeValue(key, entry))
      .filter(Boolean)
      .join("|");

    return joined || undefined;
  }

  if (typeof value === "string") {
    const normalized = value.trim();

    if (!normalized) {
      return undefined;
    }

    if (key === PARAM_KEYS.ANSWER_VALUE && looksLikePii(normalized)) {
      return undefined;
    }

    return normalized.slice(0, 120);
  }

  return undefined;
}

function sanitizeParams(params = {}) {
  return Object.entries(params).reduce((accumulator, [key, value]) => {
    if (BLOCKED_KEYS.has(key)) {
      return accumulator;
    }

    const sanitizedValue = sanitizeValue(key, value);

    if (sanitizedValue !== undefined) {
      accumulator[key] = sanitizedValue;
    }

    return accumulator;
  }, {});
}

function buildPayload(params = {}) {
  const brand =
    typeof window !== "undefined"
      ? resolveBrandFromHostname(window.location.hostname)
      : null;

  return sanitizeParams({
    [PARAM_KEYS.APP_NAME]: APP_NAME,
    brand_key: brand?.brandKey,
    ...persistentContext,
    ...userContext,
    ...pageContext,
    ...params,
  });
}

export function setPersistentContext(params = {}) {
  persistentContext = sanitizeParams({
    ...persistentContext,
    ...params,
  });

  debugLog("set_persistent_context", persistentContext);
}

export function clearPersistentContext(keys) {
  if (!Array.isArray(keys) || keys.length === 0) {
    persistentContext = {};
    return;
  }

  persistentContext = Object.entries(persistentContext).reduce((accumulator, [key, value]) => {
    if (!keys.includes(key)) {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});
}

export function setUserContext(params = {}) {
  userContext = sanitizeParams({
    ...userContext,
    ...params,
  });

  const gtag = getGtag();

  if (gtag) {
    gtag("set", "user_properties", userContext);
  }

  debugLog("set_user_context", userContext);
}

export function setPageContext(params = {}) {
  pageContext = sanitizeParams(params);
  debugLog("set_page_context", pageContext);
}

export function trackEvent(eventName, params = {}) {
  if (!eventName) {
    return;
  }

  const payload = buildPayload(params);
  const gtag = getGtag();

  debugLog(eventName, payload);

  if (gtag) {
    gtag("event", eventName, payload);
  }
}

export function trackPageView(params = {}) {
  const payload = buildPayload({
    ...params,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
    page_path:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : undefined,
    page_title: typeof document !== "undefined" ? document.title : undefined,
  });

  debugLog(EVENT_NAMES.PAGE_VIEW, payload);

  const gtag = getGtag();

  if (gtag) {
    gtag("event", EVENT_NAMES.PAGE_VIEW, payload);
  }
}
